import React, { useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const PaymentHistoryTable = ({ payments, loading }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-blue-100 text-blue-800'
    };
    return colors?.[status] || colors?.pending;
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: 'CheckCircle',
      pending: 'Clock',
      failed: 'XCircle',
      refunded: 'RefreshCw'
    };
    return icons?.[status] || 'Clock';
  };

  const filteredPayments = payments
    ?.filter(payment => filterStatus === 'all' || payment?.payment_status === filterStatus)
    ?.sort((a, b) => {
      if (sortBy === 'date-desc') return new Date(b?.billing_date) - new Date(a?.billing_date);
      if (sortBy === 'date-asc') return new Date(a?.billing_date) - new Date(b?.billing_date);
      if (sortBy === 'amount-desc') return parseFloat(b?.total_amount) - parseFloat(a?.total_amount);
      if (sortBy === 'amount-asc') return parseFloat(a?.total_amount) - parseFloat(b?.total_amount);
      return 0;
    });

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Loading payment history...</p>
      </div>
    );
  }

  if (!payments || payments?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="Receipt" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-foreground mb-2">No Payment History</h3>
        <p className="text-muted-foreground">You don't have any payment records yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-foreground mb-1">Payment History</h2>
            <p className="text-sm text-muted-foreground">{filteredPayments?.length} payment{filteredPayments?.length !== 1 ? 's' : ''} found</p>
          </div>
          <div className="flex gap-3">
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="w-40"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </Select>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="w-40"
            >
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="amount-desc">Highest Amount</option>
              <option value="amount-asc">Lowest Amount</option>
            </Select>
          </div>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Invoice #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredPayments?.map((payment) => (
              <tr key={payment?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-foreground">
                    {payment?.invoice_number || 'N/A'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(payment?.billing_date), 'MMM dd, yyyy')}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">Membership Subscription</p>
                    <p className="text-xs text-muted-foreground capitalize">{payment?.billing_cycle || 'Monthly'} billing</p>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-semibold text-foreground">
                    ${parseFloat(payment?.total_amount || 0)?.toFixed(2)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(payment?.payment_status)}`}>
                    <Icon name={getStatusIcon(payment?.payment_status)} size={14} />
                    {payment?.payment_status?.charAt(0)?.toUpperCase() + payment?.payment_status?.slice(1)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentHistoryTable;